"use client";

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon, Filter, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { AuditLogFilters as AuditLogFiltersType } from '@/hooks/audit';

interface AuditLogFiltersProps {
  filters: AuditLogFiltersType;
  onFiltersChange: (filters: Partial<AuditLogFiltersType>) => void;
  auditLogTypes: string[];
}

export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
  filters,
  onFiltersChange,
  auditLogTypes,
}) => {
  const { t } = useTranslation();

  const handleFilterChange = (key: keyof AuditLogFiltersType, value: any) => {
    onFiltersChange({ [key]: value });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      userId: undefined,
      action: undefined,
      resource: undefined,
      status: undefined,
      startDate: undefined,
      endDate: undefined,
      search: undefined,
    });
  };

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => 
      key !== 'page' && 
      key !== 'limit' && 
      key !== 'sortBy' && 
      key !== 'sortOrder' && 
      value !== undefined && 
      value !== ''
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            {t('auditLogs.filters')}
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4 mr-1" />
                {t('common.clear')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="action">{t('auditLogs.action')}</Label>
            <Select
              value={filters.action ?? undefined}
              onValueChange={(value) =>
                handleFilterChange('action', value === '__ALL__' ? undefined : value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('auditLogs.allActions')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">{t('auditLogs.allActions')}</SelectItem>
                {auditLogTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">{t('auditLogs.status')}</Label>
            <Select
              value={filters.status ?? undefined}
              onValueChange={(value) =>
                handleFilterChange('status', value === '__ALL__' ? undefined : (value as any))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('auditLogs.allStatuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">{t('auditLogs.allStatuses')}</SelectItem>
                <SelectItem value="SUCCESS">{t('auditLogs.success')}</SelectItem>
                <SelectItem value="FAILURE">{t('auditLogs.failure')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
                <Label>{t('auditLogs.startDate')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !filters.startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.startDate ? (
                        format(new Date(filters.startDate), "PPP")
                      ) : (
                        <span>{t('auditLogs.selectDate')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.startDate ? new Date(filters.startDate) : undefined}
                      onSelect={(date) => 
                        handleFilterChange('startDate', date ? date.toISOString() : undefined)
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
          </div>
        
          <div className="space-y-2">
                <Label>{t('auditLogs.endDate')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !filters.endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {filters.endDate ? (
                        format(new Date(filters.endDate), "PPP")
                      ) : (
                        <span>{t('auditLogs.selectDate')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.endDate ? new Date(filters.endDate) : undefined}
                      onSelect={(date) => 
                        handleFilterChange('endDate', date ? date.toISOString() : undefined)
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
